import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useDispatch, useSelector } from 'react-redux';

import {
  executeWarrantyReplacement,
  previewWarrantyReplacement
} from 'src/features/deviceSlice';

const useStyles = makeStyles(() => ({
  dialogPaper: {
    width: '100%',
    maxWidth: 640,
    borderRadius: 14,
    overflow: 'hidden'
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    background: '#d9042f',
    color: '#fff'
  },
  dialogHeaderTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#fff'
  },
  closeButton: {
    color: '#fff',
    padding: 4
  },
  dialogContent: {
    padding: '20px',
    backgroundColor: '#fafbfc'
  },
  fieldGroup: {
    display: 'flex',
    gap: 12,
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      gap: 0
    }
  },
  fieldRow: {
    flex: 1,
    marginBottom: 16,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      backgroundColor: '#fff'
    }
  },
  fieldLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: '#555555',
    marginBottom: 6
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#8a8a8a',
    marginBottom: 8
  },
  panelGroup: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 12,
    marginBottom: 16,
    '@media (max-width: 600px)': {
      flexDirection: 'column'
    }
  },
  panel: {
    flex: 1,
    padding: '12px 14px',
    borderRadius: 12,
    border: '1px solid #eef1f6',
    backgroundColor: '#fff'
  },
  panelArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8a8a8a'
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    fontSize: 14,
    padding: '4px 0',
    '& > span:first-child': {
      color: '#8a8a8a'
    },
    '& > span:last-child': {
      color: '#1f2430',
      fontWeight: 600,
      textAlign: 'right'
    }
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 12,
    backgroundColor: '#fdecea',
    border: '1px solid #f8d0d0',
    marginBottom: 16
  },
  errorText: {
    fontSize: 14,
    color: '#8a4a4a',
    lineHeight: 1.5
  },
  targetBox: {
    padding: '12px 14px',
    borderRadius: 12,
    backgroundColor: '#e8f0fe',
    border: '1px solid #cfe0fb',
    marginBottom: 16,
    fontSize: 14,
    color: '#1f2430'
  },
  successBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '16px 0'
  },
  successTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1f2430',
    margin: '8px 0'
  },
  successText: {
    fontSize: 14,
    color: '#555555',
    lineHeight: 1.6
  },
  dialogActions: {
    padding: '14px 20px',
    backgroundColor: '#fff',
    borderTop: '1px solid #eef1f6',
    gap: 8
  },
  primaryActionButton: {
    minWidth: 120,
    height: 40,
    borderRadius: 10,
    fontWeight: 800,
    fontStyle: 'normal',
    textTransform: 'none',
    boxShadow: 'none',
    background: '#d9042f',
    color: '#fff',
    '&:hover': {
      background: '#b80327'
    },
    '&.Mui-disabled': {
      background: '#f2b9c4',
      color: '#fff'
    }
  },
  secondaryActionButton: {
    minWidth: 100,
    height: 40,
    borderRadius: 10,
    fontWeight: 800,
    fontStyle: 'normal',
    textTransform: 'none',
    background: 'none',
    border: '1px solid #D9042F',
    color: '#D9042F',
    '&:hover': {
      border: '1px solid #D9042F',
      background: '#fdecea'
    }
  }
}));

export default function WarrantyReplacementModal({ open, onClose, onSuccess, device }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const dataLogin = useSelector(state => state.authSlice?.dataLogin);

  const [faultySerial, setFaultySerial] = useState('');
  const [replacementSerial, setReplacementSerial] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExecute, setLoadingExecute] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const isBusy = loadingPreview || loadingExecute;

  useEffect(() => {
    if (!open) return;
    setFaultySerial(device?.serial || '');
    setReplacementSerial('');
    setPreview(null);
    setError('');
    setSuccess(null);
    setLoadingPreview(false);
    setLoadingExecute(false);
  }, [open, device]);

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const handlePreview = async () => {
    if (!faultySerial.trim() || !replacementSerial.trim()) {
      setError('Vui lòng nhập đầy đủ Serial thiết bị lỗi và Serial thiết bị thay thế');
      return;
    }
    if (faultySerial.trim() === replacementSerial.trim()) {
      setError('Hai serial phải khác nhau');
      return;
    }
    setError('');
    setPreview(null);
    setLoadingPreview(true);
    try {
      const res = await dispatch(
        previewWarrantyReplacement({
          faulty_serial: faultySerial.trim(),
          replacement_serial: replacementSerial.trim()
        })
      ).unwrap();
      const payload = res?.payload || res;
      setPreview(payload);
    } catch (err) {
      const msg = typeof err === 'string'
        ? err
        : err?.message || 'Không thể kiểm tra đổi thiết bị bảo hành. Vui lòng thử lại.';
      setError(msg);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExecute = async () => {
    if (!preview?.can_swap) return;
    try {
      setLoadingExecute(true);
      const res = await dispatch(
        executeWarrantyReplacement({
          faulty_serial: faultySerial.trim(),
          replacement_serial: replacementSerial.trim()
        })
      ).unwrap();
      const resData = res?.payload || res;
      setSuccess(resData);
      onSuccess && onSuccess(resData);
    } catch (err) {
      const msg = typeof err === 'string'
        ? err
        : err?.message || 'Đổi thiết bị bảo hành thất bại. Vui lòng thử lại.';
      setError(msg);
    } finally {
      setLoadingExecute(false);
    }
  };

  const handleChangeSerial = setter => e => {
    setter(e.target.value);
    setPreview(null);
  };

  const renderDevicePanel = (panelTitle, item) => (
    <Box className={classes.panel}>
      <Typography className={classes.sectionLabel}>{panelTitle}</Typography>
      {item ? (
        <>
          <Box className={classes.row}>
            <span>Serial</span>
            <span>{item.serial}</span>
          </Box>
          <Box className={classes.row}>
            <span>Trạng thái</span>
            <span>{item.status}</span>
          </Box>
          {item.sim && (
            <Box className={classes.row}>
              <span>SIM</span>
              <span>{item.sim}</span>
            </Box>
          )}
          <Box className={classes.row}>
            <span>Loại thiết bị</span>
            <span>{item.device_type_name || '--'}</span>
          </Box>
          <Box className={classes.row}>
            <span>Biển số</span>
            <span>{item.license_plate || '--'}</span>
          </Box>
          <Box className={classes.row}>
            <span>Đại lý</span>
            <span>{item.agency_name || '--'}</span>
          </Box>
        </>
      ) : (
        <Typography className={classes.errorText}>Không tìm thấy</Typography>
      )}
    </Box>
  );

  const renderError = content => (
    <Box className={classes.errorBox}>
      <WarningIcon fontSize="small" style={{ color: '#d9042f', flexShrink: 0, marginTop: 1 }} />
      <Box className={classes.errorText}>{content}</Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.dialogHeader}>
        <Typography className={classes.dialogHeaderTitle}>
          Thay thế thiết bị bảo hành
        </Typography>
        <IconButton
          size="small"
          className={classes.closeButton}
          onClick={handleClose}
          disabled={isBusy}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        {success ? (
          <Box className={classes.successBox}>
            <CheckCircleIcon style={{ color: '#3DB670', fontSize: 56 }} />
            <Typography className={classes.successTitle}>
              Đổi thiết bị bảo hành thành công!
            </Typography>
            <Typography className={classes.successText}>
              Biển số <strong>{success.license_plate}</strong> đã chuyển từ serial{' '}
              <strong>{success.faulty_serial}</strong> sang{' '}
              <strong>{success.replacement_serial}</strong>.
            </Typography>

            {success.mqtt_published === false && (
              <Box mt={2} width="100%" textAlign="left">
                {renderError(
                  'Đổi thành công nhưng cần kiểm tra kết nối thiết bị (MQTT chưa đồng bộ).'
                )}
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Box className={classes.fieldGroup}>
              <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Serial thiết bị lỗi</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Nhập serial thiết bị lỗi"
                  value={faultySerial}
                  onChange={handleChangeSerial(setFaultySerial)}
                  disabled={isBusy}
                />
              </Box>
              <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Serial thiết bị thay thế</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Nhập serial thiết bị thay thế"
                  value={replacementSerial}
                  onChange={handleChangeSerial(setReplacementSerial)}
                  disabled={isBusy}
                />
              </Box>
            </Box>

            {error && renderError(error)}

            {preview && (
              <>
                <Box className={classes.panelGroup}>
                  {renderDevicePanel('Thiết bị lỗi', preview.faulty)}
                  <Box className={classes.panelArrow}>
                    <ArrowForwardIcon />
                  </Box>
                  {renderDevicePanel('Thiết bị thay thế trong kho', preview.replacement)}
                </Box>

                {preview.can_swap && preview.after && (
                  <Box className={classes.targetBox}>
                    <Typography className={classes.sectionLabel} style={{ color: '#45485E' }}>
                      Sau khi đổi
                    </Typography>
                    <Box className={classes.row}>
                      <span>Thiết bị phục vụ phương tiện</span>
                      <span>
                        {preview.after.active_device?.serial} (
                        {preview.after.active_device?.license_plate || '--'})
                      </span>
                    </Box>
                    <Box className={classes.row}>
                      <span>Thiết bị thu hồi về kho</span>
                      <span>
                        {preview.after.stock_device?.serial} (
                        {preview.after.stock_device?.agency_name || '--'})
                      </span>
                    </Box>
                  </Box>
                )}

                {!preview.can_swap &&
                  renderError(
                    <>
                      <strong>Không thể đổi thiết bị</strong>
                      {(preview.issues || []).map((issue, idx) => (
                        <div key={idx}>• {issue}</div>
                      ))}
                    </>
                  )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {success ? (
          <Button className={classes.primaryActionButton} onClick={handleClose}>
            Đóng
          </Button>
        ) : (
          <>
            <Button
              className={classes.secondaryActionButton}
              onClick={handleClose}
              disabled={isBusy}
            >
              Hủy
            </Button>
            {preview?.can_swap ? (
              <Button
                className={classes.primaryActionButton}
                onClick={handleExecute}
                disabled={isBusy}
                startIcon={loadingExecute ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Xác nhận đổi
              </Button>
            ) : (
              <Button
                className={classes.primaryActionButton}
                onClick={handlePreview}
                disabled={isBusy}
                startIcon={loadingPreview ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Kiểm tra
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
